import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Modal, Table } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaClock, FaExclamationTriangle } from "react-icons/fa";
import Loader from "react-spinner-loader";
import "./ManageSla.css";
import { createSLA, updateSLA, getThemeURL, getAllSLA, deleteSLAById } from "../../../Services/EndPoints";
import { getData, postDataWithBody, putDataWithBody } from "../../../Services/Services";
import { OverlayTrigger, Tooltip } from "react-bootstrap";


// Enhanced validation schema with time parsing
const slaSchema = yup.object().shape({
  priority: yup
    .string()
    .required("Priority is required")
    .trim()
    .min(2, "Priority must be at least 2 characters")
    .max(30, "Priority must not exceed 30 characters")
    .matches(/^[a-zA-Z\s]+$/, "Priority must contain only letters and spaces"),
  respondValue: yup
    .number()
    .typeError("Please specify the response time limit")
    .required("Response time value is required")
    .positive("Must be a positive number")
    .integer("Must be a whole number"),
  respondUnit: yup.string().required("Response time unit is required"),
  resolveValue: yup
    .number()
    .typeError("Please specify the resolution time limit")
    .required("Resolution time value is required")
    .positive("Must be a positive number")
    .integer("Must be a whole number"),
  resolveUnit: yup.string().required("Resolution time unit is required"),
  escalationAction: yup.string().required("Escalation Description is required"),
  status: yup.boolean(),
});

const ManageSla = () => {
  const [slaList, setSlaList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSLA, setCurrentSLA] = useState(null);
  const [loader, setLoader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [timeWarning, setTimeWarning] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(slaSchema),
    defaultValues: {
      priority: "",
      respondValue: "",
      respondUnit: "Minutes",
      resolveValue: "",
      resolveUnit: "Hours",
      escalationAction: "",
      status: true,
    },
  });

  // Watch time fields for validation
  const respondValue = watch("respondValue");
  const respondUnit = watch("respondUnit");
  const resolveValue = watch("resolveValue");
  const resolveUnit = watch("resolveUnit");

  // Predefined priority suggestions (optional helpers)
  const prioritySuggestions = [
    { value: "Critical", color: "#dc3545" },
    { value: "Major", color: "#fd7e14" },
    { value: "Significant", color: "#ffc107" },
  ];

  // Time unit options - Updated to match API format
  const timeUnits = [
    { value: "Minutes", label: "Minutes" },
    { value: "Hours", label: "Hours" },
    { value: "Days", label: "Days" },
  ];

  // Escalation templates
  const escalationTemplates = [
    "Notify the L1 User",
    "Notify the L2 User",
    "Notify the L3 User",
    "Notify the HO Admin",
    "Notify the Super Admin",
  ];

  // Load SLA data and user profile on component mount
  useEffect(() => {
    getUserProfile();
  }, []);

  // Load SLA data after userId is set
  useEffect(() => {
    if (userId) {
      loadSLAData();
    }
  }, [userId]);

  // Get user profile to retrieve userId for API calls
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      console.log("User Profile Response:", userProfile);
      if (userProfile && Array.isArray(userProfile) && userProfile.length > 0) {
        setUserId(userProfile[0].id);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Validate time difference
  useEffect(() => {
    if (respondValue && respondUnit && resolveValue && resolveUnit) {
      const respondMinutes = convertToMinutes(respondValue, respondUnit);
      const resolveMinutes = convertToMinutes(resolveValue, resolveUnit);

      if (resolveMinutes <= respondMinutes) {
        setTimeWarning("Resolution time should be greater than response time");
      } else {
        setTimeWarning("");
      }
    }
  }, [respondValue, respondUnit, resolveValue, resolveUnit]);

  const convertToMinutes = (value, unit) => {
    const val = parseInt(value);
    switch (unit) {
      case "Minutes":
        return val;
      case "Hours":
        return val * 60;
      case "Days":
        return val * 24 * 60;
      default:
        return 0;
    }
  };

  const formatTimeDisplay = (value, unit) => {
    return `${value} ${unit}`;
  };

  // Load SLA data from API using getData method
  const loadSLAData = async () => {
    setLoader(true);
    try {
      const response = await getData(getAllSLA);
      console.log("SLA Data Response:", response);

      if (response && Array.isArray(response)) {
        // Filter out deleted records if needed
        const activeSLAs = response.filter(sla => !sla.is_deleted);
        setSlaList(activeSLAs);
      } else if (response && response.data && Array.isArray(response.data)) {
        // If response has a data property
        const activeSLAs = response.data.filter(sla => !sla.is_deleted);
        setSlaList(activeSLAs);
      } else {
        console.warn("Unexpected SLA data format:", response);
        setSlaList([]);
        toast.info("No SLA configurations found", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error loading SLA data:", error);
      toast.error("Failed to load SLA configurations", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      setSlaList([]);
    } finally {
      setLoader(false);
    }
  };

  const handleAddNew = () => {
    setEditMode(false);
    setCurrentSLA(null);
    setTimeWarning("");
    reset({
      priority: "",
      respondValue: "",
      respondUnit: "Minutes",
      resolveValue: "",
      resolveUnit: "Hours",
      escalationAction: "",
      status: true,
    });
    setShowModal(true);
  };

  const handleEdit = (sla) => {
    setEditMode(true);
    setCurrentSLA(sla);
    setTimeWarning("");

    setValue("priority", sla.priority);
    setValue("respondValue", parseInt(sla.minResTimeValue));
    setValue("respondUnit", sla.minResTimeType);
    setValue("resolveValue", parseInt(sla.maxResTimeValue));
    setValue("resolveUnit", sla.maxResTimeType);
    setValue("escalationAction", sla.escalationAction);
    setValue("status", sla.activeStatus);
    setShowModal(true);
  };

  // const handleDelete = (id) => {
  //   setDeleteId(id);
  //   setShowDeleteModal(true);
  // };
  const handleDelete = (sla) => {
    setDeleteId(sla.id);
    setDeleteMessage(`Are you sure you want to delete this SLA ${sla.priority}? This action cannot be undone.`);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoader(true);
    try {
      const deletePayload = {
        id: deleteId,
        updated_by: userId
      };

      const response = await putDataWithBody(deleteSLAById, deletePayload, {});

      if (response && response.status === "success") {
        toast.success(response.msg || "SLA configuration deleted successfully", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        await loadSLAData();
      } else {
        toast.error(response.msg || "Failed to delete SLA configuration", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error deleting SLA:", error);
      toast.error("Failed to delete SLA configuration", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
      setLoader(false);
    }
  };

  const onSubmit = async (data) => {
    // Check if userId is available
    if (!userId) {
      toast.error("User information not available. Please refresh the page.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      return;
    }

    // Final validation for time difference
    if (data.respondValue) {
    const respondMinutes = convertToMinutes(data.respondValue, data.respondUnit);
    const resolveMinutes = convertToMinutes(data.resolveValue, data.resolveUnit);

    if (resolveMinutes <= respondMinutes) {
      toast.error("Resolution time must be greater than response time", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }
  }
    setLoader(true);
    setIsSubmitting(true);

    try {
      // Prepare data payload matching the API requirements
      const formattedData = {
        priority: data.priority.trim(),
        minResTimeValue: data.respondValue.toString(),
        minResTimeType: data.respondUnit,
        maxResTimeValue: data.resolveValue.toString(),
        maxResTimeType: data.resolveUnit,
        escalationAction: data.escalationAction,
        is_deleted: false,
        activeStatus: data.status,
        createdBy: userId,
      };

      let response;

      if (editMode) {
        // Update existing SLA
        formattedData.id = currentSLA.id;
        formattedData.updatedBy = userId;
        delete formattedData.createdBy;

        response = await putDataWithBody(updateSLA, formattedData, {});

        if (response && response.status === "success") {
          toast.success(response.msg || "SLA configuration updated successfully", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
          await loadSLAData();
          setShowModal(false);
          reset();
          setTimeWarning("");
        } else {
          toast.error(response.msg || "Failed to update SLA configuration", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } else {
        // Create new SLA
        response = await postDataWithBody(createSLA, formattedData, {});

        if (response && response.status === "success") {
          toast.success(response.msg || "SLA configuration added successfully", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
          await loadSLAData();
          setShowModal(false);
          reset();
          setTimeWarning("");
        } else {
          toast.error(response.msg || "Failed to add SLA configuration", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      }
    } catch (error) {
      console.error("Error saving SLA:", error);
      toast.error("Failed to save SLA configuration", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setLoader(false);
    }
  };

  const getPriorityBadgeColor = (priority) => {
    const suggestion = prioritySuggestions.find(
      (opt) => opt.value.toLowerCase() === priority.toLowerCase()
    );
    return suggestion ? suggestion.color : "#6c757d";
  };

  const handleTemplateSelect = (template) => {
    setValue("escalationAction", template);
  };

  const handlePrioritySuggestionClick = (priorityValue) => {
    setValue("priority", priorityValue);
  };

  return (
    <div className="sla-matrix-container">
      <Loader show={loader} type="body" stack="vertical" message="Processing..." />

      {/* Header */}
      <div className="sla-header">
        <button className="btn-add-sla" onClick={handleAddNew}>
          <FaPlus /> Add New SLA
        </button>
      </div>

      {/* Table */}
      <div className="sla-table-container">
        <Table responsive bordered className="sla-table">
          <thead>
            <tr>
              <th style={{ width: "15%" }}>Priority</th>
              <th style={{ width: "18%" }}>
                <FaClock className="header-icon" /> Respond Within
              </th>
              <th style={{ width: "18%" }}>
                <FaClock className="header-icon" /> Resolve Within
              </th>
              <th style={{ width: "30%" }}>Escalation Description</th>
              <th style={{ width: "10%" }}>Status</th>
              <th style={{ width: "9%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slaList.length > 0 ? (
              slaList.map((sla) => (
                <tr key={sla.id}>
                  <td>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityBadgeColor(sla.priority) }}
                    >
                      {sla.priority}
                    </span>
                  </td>
                  <td>
                    <span className="time-badge">
                      <FaClock /> {formatTimeDisplay(sla.minResTimeValue, sla.minResTimeType)}
                    </span>
                  </td>
                  <td>
                    <span className="time-badge">
                      <FaClock /> {formatTimeDisplay(sla.maxResTimeValue, sla.maxResTimeType)}
                    </span>
                  </td>
                  <td>{sla.escalationAction}</td>
                  <td>
                    <span className={`status-badge ${sla.activeStatus ? "active" : "inactive"}`}>
                      {sla.activeStatus ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {/* Edit Button with Tooltip */}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          sla.disable ? (
                            <Tooltip id={`tooltip-edit-${sla.id}`}>
                              Cannot edit linked to active tickets.
                            </Tooltip>
                          ) : (
                            <></>
                          )
                        }
                        delay={{ show: 300, hide: 100 }}
                      >
                        {/* Wrap the button in a span to capture hover events even when disabled */}
                        <span style={{ display: "inline-block" }}>
                          <button
                            className="btn-action btn-edit"
                            onClick={() => handleEdit(sla)}
                            disabled={sla.disable}
                            style={{
                              opacity: sla.disable ? 0.5 : 1,
                              cursor: sla.disable ? "not-allowed" : "pointer",
                            }}
                          >
                            <FaEdit />
                          </button>
                        </span>
                      </OverlayTrigger>

                      {/* Delete Button with Tooltip */}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          sla.disable ? (
                            <Tooltip id={`tooltip-delete-${sla.id}`}>
                              Cannot delete linked to active tickets.
                            </Tooltip>
                          ) : (
                            <></>
                          )
                        }
                        delay={{ show: 300, hide: 100 }}
                      >
                        <span style={{ display: "inline-block" }}>
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDelete(sla)}
                            disabled={sla.disable}
                            style={{
                              opacity: sla.disable ? 0.5 : 1,
                              cursor: sla.disable ? "not-allowed" : "pointer",
                            }}
                          >
                            <FaTrash />
                          </button>
                        </span>
                      </OverlayTrigger>
                    </div>

                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No SLA configurations found. Click "Add New SLA" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "Edit SLA Configuration" : "Add New SLA Configuration"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <div className="sla-form">
              {/* Priority - Now Input Field */}
              <div className="form-group">
                <label className="form-label">
                  Priority Level <span className="required">*</span>
                </label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="form-control"
                      placeholder="Enter priority name (e.g., Critical, Major, Custom Priority)"
                      maxLength={30}
                    />
                  )}
                />
                {errors.priority && (
                  <span className="error-message">{errors.priority.message}</span>
                )}

                {/* Priority Suggestions */}
                <div className="priority-suggestions">
                  <small className="form-text">Quick suggestions:</small>
                  <div className="template-chips" style={{ marginTop: "8px" }}>
                    {prioritySuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        className="template-chip"
                        style={{
                          borderColor: suggestion.color,
                          color: suggestion.color,
                        }}
                        onClick={() => handlePrioritySuggestionClick(suggestion.value)}
                      >
                        {suggestion.value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Response Time - Split Input */}
              <div className="form-group">
                <label className="form-label">
                  Respond Within<span className="required">*</span>
                </label>
                <div className="time-input-group">
                  <div className="time-value">
                    <Controller
                      name="respondValue"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          className="form-control"
                          placeholder="Value"
                          min="1"
                          onWheel={e => e.target.blur()}
                        />
                      )}
                    />
                  </div>
                  <div className="time-unit">
                    <Controller
                      name="respondUnit"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className="form-control">
                          {timeUnits.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>
                {errors.respondValue && (
                  <span className="error-message">{errors.respondValue.message}</span>
                )}
                {errors.respondUnit && (
                  <span className="error-message">{errors.respondUnit.message}</span>
                )}
              </div>

              {/* Resolution Time - Split Input */}
              <div className="form-group">
                <label className="form-label">
                  Resolve Within<span className="required">*</span>
                </label>
                <div className="time-input-group">
                  <div className="time-value">
                    <Controller
                      name="resolveValue"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="number"
                          className="form-control"
                          placeholder="Value"
                          min="1"
                          onWheel={e => e.target.blur()}
                        />
                      )}
                    />
                  </div>
                  <div className="time-unit">
                    <Controller
                      name="resolveUnit"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className="form-control">
                          {timeUnits.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>
                {errors.resolveValue && (
                  <span className="error-message">{errors.resolveValue.message}</span>
                )}
                {errors.resolveUnit && (
                  <span className="error-message">{errors.resolveUnit.message}</span>
                )}
                {timeWarning && (
                  <div className="time-warning">
                    <FaExclamationTriangle /> {timeWarning}
                  </div>
                )}
              </div>

              {/* Escalation Templates */}
              <div className="form-group">
                <label className="form-label">Quick Templates (Optional)</label>
                <div className="template-chips">
                  {escalationTemplates.map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      className="template-chip"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {template}
                    </button>
                  ))}
                </div>
              </div>

              {/* Escalation Action */}
              <div className="form-group">
                <label className="form-label">
                  Escalation Description<span className="required">*</span>
                </label>
                <Controller
                  name="escalationAction"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      className="form-control"
                      rows="3"
                      placeholder="Describe the escalation process and who should be notified"
                    />
                  )}
                />
                {errors.escalationAction && (
                  <span className="error-message">{errors.escalationAction.message}</span>
                )}
              </div>

              {/* Status */}
              <div className="form-group">
                <label className="checkbox-label">
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        {...field}
                        checked={field.value}
                        className="form-checkbox"
                      />
                    )}
                  />
                  <span className="checkbox-text">Active Status</span>
                </label>
                <small className="form-text">
                  Inactive SLA configurations will not be applied
                </small>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setShowModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={!!timeWarning || isSubmitting}
            >
              {editMode ? "Update SLA" : "Add SLA"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{margin:"0"}}>{deleteMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button className="btn-delete-confirm" onClick={confirmDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageSla;